import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';

import { api } from '../../../lib/api.js';

export function OperationsPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const [confirm, setConfirm] = useState('');
  const [tiers, setTiers] = useState<string[]>(['memory', 'redis']);
  const [reveal, setReveal] = useState<{ value: string; ttl: number } | null>(null);

  const flush = useMutation({
    mutationFn: () =>
      api.post(
        `/api/workspaces/${encodeURIComponent(id)}/operations/cache`,
        { tiers, confirm_client_id: confirm },
        undefined,
        crypto.randomUUID(),
      ),
  });

  const rotate = useMutation({
    mutationFn: async (which: 'secret' | 'access_key') => {
      const res = await api.post<{ reveal_id: string; ttl_seconds: number }>(
        `/api/workspaces/${encodeURIComponent(id)}/operations/keys/rotate`,
        { which },
        undefined,
        crypto.randomUUID(),
      );
      const v = await api.get<{ value: string }>(
        `/api/workspaces/${encodeURIComponent(id)}/operations/reveal/${res.reveal_id}`,
      );
      setReveal({ value: v.value, ttl: res.ttl_seconds });
    },
  });

  return (
    <div className="space-y-4">
      <div className="card">
        <h3 className="text-base font-semibold">Flush cache</h3>
        <p className="mt-1 text-sm text-slate-500">
          Type the workspace id to confirm. Sensitive action.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          {(['memory', 'redis', 'filesystem', 'varnish', 'cloudfront'] as const).map((t) => (
            <label key={t} className="flex items-center gap-1 text-sm">
              <input
                type="checkbox"
                checked={tiers.includes(t)}
                onChange={(e) =>
                  setTiers((prev) =>
                    e.target.checked ? [...prev, t] : prev.filter((x) => x !== t),
                  )
                }
              />
              {t}
            </label>
          ))}
        </div>
        <input
          className="input mt-3"
          placeholder={id}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />
        <button
          className="btn-danger mt-3"
          disabled={confirm !== id || tiers.length === 0 || flush.isPending}
          onClick={() => flush.mutate()}
        >
          {flush.isPending ? 'Flushing…' : 'Flush'}
        </button>
        {flush.error && (
          <p className="mt-2 text-sm text-red-600">{(flush.error as Error).message}</p>
        )}
      </div>

      <div className="card">
        <h3 className="text-base font-semibold">Rotate keys</h3>
        <div className="mt-3 flex gap-3">
          <button
            className="btn-secondary"
            disabled={rotate.isPending}
            onClick={() => rotate.mutate('secret')}
          >
            Rotate secret
          </button>
          <button
            className="btn-secondary"
            disabled={rotate.isPending}
            onClick={() => rotate.mutate('access_key')}
          >
            Rotate access key
          </button>
        </div>
        {reveal && (
          <div className="mt-4 rounded border border-amber-300 bg-amber-50 p-3 text-sm">
            <p className="font-semibold text-amber-800">
              Copy this value now. It will not be shown again.
            </p>
            <code className="mt-2 block break-all rounded bg-white p-2 font-mono text-xs">
              {reveal.value}
            </code>
            <button className="btn-secondary mt-3 text-xs" onClick={() => setReveal(null)}>
              I have saved it, close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
